/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";

import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { v4 as uuid_v4 } from "uuid";

import { Panel } from "../../../../ui/components/Panel";
import {
	ITreeNode,
	TREE_VIEW_STYLE,
	TTreeViewNodeDragStartHandler
} from "../../../../ui/components/Tree/TreeViewTypes";
import { TreeView } from "../../../../ui/components/Tree/TreeView";
import { useTreeState } from "../../../../ui/components/Tree/useTreeState";
import { EndpointEditorEditView } from "../EndpointEditorEditView";
import { useBlueprintModel } from "../../../../ui/hooks/useBlueprintModel";
import { FLOW_EDITOR_DND_MIMETYPE } from "../../../../ui/components/FlowEditor/FlowEditor";
import { ISchemaFlowNodeListModel } from "@adapptio/lib-blueprint";
import { TBlueprintEndpointNodeTypes } from "@adapptio/lib-core";

enum PALETTE_ITEM_TYPE {
	BASE_NODE = "baseNode",
	INTEGRATION_FUNCTION = "integrationFunction"
}

export interface IPaletteTreeNode extends ITreeNode {
	metaData?: {
		paletteItemType?: PALETTE_ITEM_TYPE;
		nodeType?: string;
		integrationType?: string;
		functionName?: string;
		integrationId?: string;
	};
}

function getTreeNodes(
	t: TTranslateHookFn,
	flowListModel: ISchemaFlowNodeListModel<TBlueprintEndpointNodeTypes>
): IPaletteTreeNode[] {
	const nodeTypes = flowListModel.schema.opts.nodeTypes;

	const baseNodes: IPaletteTreeNode[] = [];

	const rootClass = BEM("edt-module-endpoint-editor-node-palette__tree-node-section");
	const functionClass = BEM("edt-module-endpoint-editor-node-palette__tree-node-function");

	// Add base node types
	for (const k in nodeTypes) {
		if (!nodeTypes[k].editorOptions?.displayInPalette) {
			continue;
		}

		baseNodes.push({
			id: "node_" + k,
			label: t("editor", nodeTypes[k].label),
			icon: nodeTypes[k].icon,
			children: null,
			isLoadable: false,
			selectable: true,
			metaData: {
				paletteItemType: PALETTE_ITEM_TYPE.BASE_NODE,
				nodeType: k
			},
			className: functionClass.toClassName()
		});
	}

	// Add root nodes
	return [
		{
			id: "baseNodes",
			label: "Common Functions",
			defaultExpanded: true,
			children: baseNodes,
			className: rootClass.toClassName()
		}
	];
}

export interface IEndpointEditorNodePaletteProps {
	view: EndpointEditorEditView;
}

export const EndpointEditorEditNodePalette: React.FunctionComponent<IEndpointEditorNodePaletteProps> = ({
	view
}) => {
	const t = useTranslate();

	const { model: flowListModel } = useBlueprintModel(
		view.getEndpointModel().getModel().props.spec.constant.props.nodes.value,
		"flowNodeList"
	);

	const [ treeState, handleTreeStateUpdate ] = useTreeState({
		nodes: {
			baseNodes: { expanded: true },
			integrationNodes: { expanded: true }
		}
	});

	const treeNodes = useMemo(() => {
		if (flowListModel) {
			return getTreeNodes(t, flowListModel);
		}
	}, [ t, view, flowListModel ]);

	const isDraggable = useCallback((node: ITreeNode) => {
		return !!node.metaData?.paletteItemType;
	}, []);

	const allowDrop = useCallback(() => false, []);

	const handleDragStart = useCallback<TTreeViewNodeDragStartHandler>((evData) => {
		const node = evData.draggedNode as IPaletteTreeNode;

		switch (node.metaData?.paletteItemType) {
			case PALETTE_ITEM_TYPE.BASE_NODE: {
				evData.event.dataTransfer.setData(
					FLOW_EDITOR_DND_MIMETYPE,
					JSON.stringify({
						id: uuid_v4(),
						varName: null,
						type: node.metaData.nodeType,
						opts: {},
						outputs: {},
						position: {
							x: 0,
							y: 0
						}
					})
				);
				evData.event.dataTransfer.effectAllowed = "move";

				break;
			}
		}
	}, []);

	const baseClass = BEM("edt-module-endpoint-editor-node-palette");

	return (
		<Panel scroll={true} className={baseClass.toClassName()}>
			<TreeView
				items={treeNodes}
				state={treeState}
				onStateUpdate={handleTreeStateUpdate}
				onNodeDragStart={handleDragStart}
				isDraggable={isDraggable}
				allowDrop={allowDrop}
				style={TREE_VIEW_STYLE.DEFAULT}
				compact={false}
			/>
		</Panel>
	);
};
